import { OfflineDbService } from './offline-db.service';
import { OFFLINE_DATA_FORMAT, DATA_FORMAT_ID } from './offline-db.types';
import { getCurrentSchoolSubdomain, getCurrentUserId } from './offline-cache-key';
import { isInstalledPwa } from './offline-display-mode';

// A readable snapshot of every condition offline notes depend on.
//
// This exists because the people hitting the bugs are on phones, where there is no console to
// open and no storage inspector to look at. Every round of "it doesn't work on the installed
// app" so far has been spent guessing which of five preconditions failed, because all five
// fail the same way on screen: an empty page. Reading a saved note needs the school and user
// to resolve, a valid unexpired entitlement, an index entry, the content record, and a device
// key that still decrypts it — and nothing distinguished them.
//
// Rendered by the downloads page, so it works with no connection and can simply be screenshotted.
export interface OfflineDiagnostics {
  school: string | null;
  userId: number | null;
  loggedIn: boolean;
  entitlementPresent: boolean;
  entitlementExpiresAt: string | null;
  entitlementExpired: boolean | null;
  entitlementModuleCount: number | null;
  savedLessons: number;
  savedNotes: number;
  deviceKeyPresent: boolean;
  dataFormatStored: number | null;
  dataFormatExpected: number;
  serviceWorkerSupported: boolean;
  serviceWorkerScopes: string[];
  installedApp: boolean;
  displayModes: string;
  onLine: boolean;
  storageEstimate: string | null;
  collectedAt: string;
}

const DISPLAY_MODES = ['standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay', 'browser'];

function describeDisplayModes(): string {
  if (typeof window.matchMedia !== 'function') return 'matchMedia unavailable';
  const matched = DISPLAY_MODES.filter((mode) => {
    try {
      return window.matchMedia(`(display-mode: ${mode})`).matches;
    } catch {
      return false;
    }
  });
  return matched.length ? matched.join(', ') : 'none reported';
}

// Never throws and never leaves a field undefined — a diagnostic that can itself fail is worse
// than none, because it turns one unknown into two.
export async function collectOfflineDiagnostics(db: OfflineDbService, token: string | null): Promise<OfflineDiagnostics> {
  const school = getCurrentSchoolSubdomain();
  const userId = getCurrentUserId(token);

  const diagnostics: OfflineDiagnostics = {
    school,
    userId,
    loggedIn: !!token,
    entitlementPresent: false,
    entitlementExpiresAt: null,
    entitlementExpired: null,
    entitlementModuleCount: null,
    savedLessons: 0,
    savedNotes: 0,
    deviceKeyPresent: false,
    dataFormatStored: null,
    dataFormatExpected: OFFLINE_DATA_FORMAT,
    serviceWorkerSupported: !!navigator.serviceWorker,
    serviceWorkerScopes: [],
    installedApp: isInstalledPwa(),
    displayModes: describeDisplayModes(),
    onLine: navigator.onLine,
    storageEstimate: null,
    collectedAt: new Date().toISOString(),
  };

  if (school && userId) {
    try {
      const entitlement = await db.getEntitlement(`${school}:${userId}`);
      if (entitlement) {
        diagnostics.entitlementPresent = true;
        diagnostics.entitlementExpiresAt = new Date(entitlement.expiresAt).toISOString();
        diagnostics.entitlementExpired = entitlement.expiresAt < Date.now();
        diagnostics.entitlementModuleCount = entitlement.moduleIds?.length ?? 0;
      }
    } catch (err) {
      console.error('[offline] diagnostics: entitlement read failed', err);
    }

    try {
      const entries = await db.listIndexEntries(school, userId);
      diagnostics.savedLessons = entries.filter((e) => e.kind === 'lesson' && e.status === 'saved').length;
      diagnostics.savedNotes = entries.filter((e) => e.kind === 'note' && e.status === 'saved').length;
    } catch (err) {
      console.error('[offline] diagnostics: index read failed', err);
    }
  }

  try {
    diagnostics.deviceKeyPresent = !!(await db.getDeviceKeyRecord());
  } catch (err) {
    console.error('[offline] diagnostics: device key read failed', err);
  }

  try {
    diagnostics.dataFormatStored = (await db.getRawKeyRecord<{ format?: number }>(DATA_FORMAT_ID))?.format ?? null;
  } catch (err) {
    console.error('[offline] diagnostics: data-format read failed', err);
  }

  try {
    const registrations = navigator.serviceWorker ? await navigator.serviceWorker.getRegistrations() : [];
    diagnostics.serviceWorkerScopes = registrations.map((r) => r.scope);
  } catch (err) {
    console.error('[offline] diagnostics: service worker read failed', err);
  }

  try {
    if (navigator.storage?.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      const mb = (n?: number) => (typeof n === 'number' ? `${(n / 1048576).toFixed(0)}MB` : '?');
      diagnostics.storageEstimate = `${mb(usage)} of ${mb(quota)}`;
    }
  } catch (err) {
    console.error('[offline] diagnostics: storage estimate failed', err);
  }

  return diagnostics;
}

// What the panel shows as a one-line verdict, so the common cases need no interpretation.
export function summariseDiagnostics(d: OfflineDiagnostics): string {
  if (!d.loggedIn) return 'Not signed in on this device — sign in here, then download a lesson.';
  if (!d.school) return 'Could not work out which school this is from the web address.';
  if (!d.serviceWorkerSupported) return 'This browser cannot store an app for offline use. Try Safari on iPhone, or Chrome on Android.';
  if (d.savedLessons === 0 && d.savedNotes === 0) {
    return 'Nothing has been downloaded on this device yet. Downloads are per-device, and the installed app may not share them with the browser.';
  }
  if (!d.entitlementPresent) return 'Downloads are here, but this device has no offline permission recorded. Reconnect once to refresh it.';
  if (d.entitlementExpired) return 'Offline access has expired. Reconnect once to renew it.';
  if (!d.deviceKeyPresent) return 'The key that unlocks downloads on this device is missing, so saved lessons cannot be read. Remove all downloads and save them again.';
  return 'Everything needed to read downloads offline is present on this device.';
}
